import React from 'react';
import Container from 'components/Container';
import clsx from 'clsx';
import type {Brand} from 'domains/brands';
import {Disclosure} from '@headlessui/react';
import SanityImage from 'components/SanityImage';
import {Link} from 'components/Link';
import CategoryIcon from 'components/global/CategoryIcon';
import {useLocation} from '@remix-run/react';
import {useMatches} from '@remix-run/react';
import {brandHref} from '~/lib/brandCollections';

interface Props {}

const MenubarDesktop: React.FC<Props> = () => {
  const [root] = useMatches();
  const menu = root?.data?.menu as any;
  const allBrands = root?.data?.allBrands as Brand[];
  const featuredBrands = root?.data?.featuredBrands as {
    featured_brands: Brand[];
  };
  return (
    <nav
      className={clsx(
        'left-0 top-0 w-full bg-hk-deep-blue text-[18px] text-white',
        // !isHome && 'hidden md:block'
        'hidden md:block',
      )}
    >
      <Container>
        <ul className="hiddenScroll flex justify-between space-x-4 overflow-y-scroll md:overflow-x-scroll md:overflow-y-hidden">
          {menu &&
            menu.map((item: any) => {
              const internal_link_slug =
                item.slug === '/blogs/all' ? '/blogs' : item.slug;
              // Even column distribution: size the mega-menu grid to the
              // actual number of populated columns instead of a fixed count,
              // so gaps stay uniform whether a menu has 2 or 6 columns.
              const megaCols = [
                item.col_1_links,
                item.col_2_links,
                item.col_3_links,
                item.col_4_links,
                item.col_5_links,
              ].filter((c: any) => c?.links?.length);
              const multiCols = (item.columns ?? []).filter(
                (c: any) => c?.column,
              );
              return (
                <li key={item._key}>
                  <MegaMenu
                    className={clsx([
                      'group relative py-3 transition-all duration-200 ease-out hover:text-hk-orange',
                      'after:absolute after:bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-hk-orange after:transition-all after:duration-200 hover:after:w-full',
                      item.title === 'Sale' && 'text-hk-red',
                      item.title === '特價貨品' && 'text-hk-red',
                      // 特賣場 (close-out) — bright red so it stands out; match
                      // by slug so it works in every locale.
                      item.title === '特賣場' && 'text-hk-red',
                      typeof item.slug === 'string' &&
                        item.slug.includes('close-out') &&
                        'text-hk-red',
                    ])}
                  >
                    {(isOpened) => (
                      <>
                        {/* {item.title === 'All Brands' && allBrands && (
                          <div
                            className={clsx(
                              isOpened
                                ? 'visible opacity-100'
                                : 'opacity-0 invisible',
                            )}
                          >
                            <AllBrands
                              allBrands={allBrands}
                              featuredBrands={featuredBrands}
                            />
                          </div>
                        )} */}
                        {item._type === 'linkMegaMenu' ? (
                          <>
                            {item.link ? (
                              <Link
                                to={item.link[0].slug}
                                className="whitespace-nowrap text-sm"
                              >
                                {item.link[0].title}
                              </Link>
                            ) : (
                              <span className="whitespace-nowrap text-sm">
                                {item.title}
                              </span>
                            )}

                            <div
                              className={clsx(
                                'fixed z-30 md:translate-y-headerDesktop desktop-menubar-hidden top-0 left-0 w-full bg-white py-7 text-black shadow-md transition-all duration-200 ease-out',
                                // 'group-hover:visible group-hover:opacity-100',
                                isOpened
                                  ? 'visible opacity-100'
                                  : 'opacity-0 invisible',
                              )}
                            >
                              <Container className="relative">
                                <div
                                  className="grid gap-x-8"
                                  style={{
                                    gridTemplateColumns: `repeat(${Math.max(
                                      megaCols.length,
                                      1,
                                    )}, minmax(0, 1fr))`,
                                  }}
                                >
                                  {megaCols.map((c: any, i: number) => (
                                    <LinkGroup key={i} links={c.links} />
                                  ))}
                                </div>
                              </Container>
                            </div>
                          </>
                        ) : item._type === 'linkInternal' ? (
                          <Link
                            to={`${internal_link_slug ?? item.slug}`}
                            className="whitespace-nowrap text-sm"
                          >
                            {item.title}
                          </Link>
                        ) : item._type === 'linkMenuMultiCols' ? (
                          <>
                            {item.link ? (
                              <Link
                                to={item.link[0].slug}
                                className="whitespace-nowrap text-sm"
                              >
                                {item.link[0].title}
                              </Link>
                            ) : (
                              <span className="whitespace-nowrap text-sm">
                                {item.title}
                              </span>
                            )}

                            <div
                              className={clsx(
                                'fixed z-30 md:translate-y-headerDesktop desktop-menubar-hidden top-0 left-0 w-full bg-white py-7 text-black shadow-md transition-all duration-200 ease-out',
                                // 'group-hover:visible group-hover:opacity-100',
                                isOpened
                                  ? 'visible opacity-100'
                                  : 'opacity-0 invisible',
                              )}
                            >
                              <Container className="relative">
                                <div
                                  className="grid gap-x-6"
                                  style={{
                                    gridTemplateColumns: `repeat(${Math.max(
                                      multiCols.length,
                                      1,
                                    )}, minmax(0, 1fr))`,
                                  }}
                                >
                                  {multiCols.map((col: any) => (
                                    <div key={col._key} className="">
                                      <LinkGroup links={col.column.link} />
                                    </div>
                                  ))}
                                </div>
                              </Container>
                            </div>
                          </>
                        ) : item._type === 'linkExternal' ? (
                          <a
                            href={`${item.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whitespace-nowrap text-sm"
                          >
                            {item.title}
                          </a>
                        ) : (
                          <a
                            href={`${item.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whitespace-nowrap text-sm"
                          >
                            {item.title}
                          </a>
                        )}
                      </>
                    )}
                  </MegaMenu>
                </li>
              );
            })}
        </ul>
      </Container>
    </nav>
  );
};

interface LinkGroupProps {
  links: any;
}

function AllBrands({
  allBrands,
  featuredBrands,
}: {
  allBrands: Brand[];
  featuredBrands: {featured_brands: Brand[]} | undefined;
}) {
  const alpha = Array.from(Array(26)).map((e, i) => i + 65);
  const alphabets = alpha.map((x) => String.fromCharCode(x));
  const availableAlphabets = alphabets.filter((a) =>
    allBrands
      .map((b) => b.title.replace(/\s/g, '').toUpperCase().charAt(0))
      .includes(a),
  );
  const brandsByInitials = [
    {
      initials: '#',
      brands: allBrands.filter(
        (b) =>
          !availableAlphabets.includes(
            b?.title?.replace(/\s/g, '')?.charAt(0)?.toUpperCase(),
          ),
      ),
    },
  ].concat(
    availableAlphabets.map((a) => {
      return {
        initials: a,
        brands: allBrands.filter(
          (b) =>
            b?.title?.replace(/\s/g, '')?.charAt(0)?.toLocaleLowerCase() ===
            a.toLocaleLowerCase(),
        ),
      };
    }),
  );

  return (
    //
    <div className="fixed z-30 md:translate-y-headerDesktop top-0 left-0 max-h-[50vh] w-full overflow-y-scroll bg-white py-7 text-black shadow-md transition-all duration-200 ease-out">
      <Container className="grid grid-cols-8 gap-16">
        <div className="col-span-6 grid border-collapse grid-cols-4">
          {brandsByInitials.map((b, i) => {
            return (
              <div
                key={`header-brand-${b.initials}-${i}`}
                className={clsx(
                  'min-h-[200px] border border-t-0 border-l-0 border-solid border-hk-orange py-4 px-6',
                  i === 0 && 'border-l-0',
                  i % 4 === 0 && 'border-l',
                  (i + 1) % 4 === 0 && 'border-r-0',
                  i === 3 && 'border-r-0',
                )}
              >
                <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-hk-orange bg-opacity-20">
                  <div className="font-bold text-hk-orange">{b.initials}</div>
                </div>
                <div className="grid gap-3 tracking-[0.015em]">
                  {b.brands
                    .filter((_, i) => i < 3)
                    .map((brand) => (
                      <Link
                        key={`header-brand-${brand.title}`}
                        to={brandHref(brand.shopify_brand_node ?? brand.title)}
                        className="font-light uppercase"
                      >
                        {brand.title}
                      </Link>
                    ))}
                  {b.brands && b.brands.length > 3 && (
                    <div className="">
                      <Disclosure defaultOpen={false}>
                        {({open}) => (
                          <>
                            <Disclosure.Panel className="grid gap-3">
                              {b.brands
                                .filter((_, i) => i > 2)
                                .map((brand) => (
                                  <Link
                                    key={`header-brand-${brand.title}-2`}
                                    to={brandHref(
                                      brand.shopify_brand_node ?? brand.title,
                                    )}
                                    className="font-light uppercase"
                                  >
                                    {brand.title}
                                  </Link>
                                ))}
                            </Disclosure.Panel>
                            <Disclosure.Button
                              className={clsx('font-light text-hk-orange')}
                            >
                              {open ? 'See less' : 'See more'}
                            </Disclosure.Button>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="col-span-2 flex flex-col space-y-8">
          <div className="mb-16 text-center font-bold uppercase italic text-hk-orange">
            Top Brands
          </div>
          {featuredBrands?.featured_brands &&
            featuredBrands?.featured_brands.map((b, i) => {
              if (b.brand_logo) {
                return (
                  <div key={`fb-${b.title}-${i}`} className="flex items-center">
                    <Link to={brandHref(b.shopify_brand_node ?? b.title)}>
                      <SanityImage
                        layout="responsive"
                        sizes={['50vw, 100vw']}
                        src={b.brand_logo?.asset._ref}
                        alt={b.title ?? ''}
                        loading="lazy"
                        className="h-full object-contain"
                      />
                    </Link>
                  </div>
                );
              }
              // if (b?.shopify_brand?.shopify_collection?.store?.imageUrl) {
              //   return (
              //     <div key={`fb-${b.title}`} className="flex items-center">
              //       <Link to={brandHref(b.shopify_brand_node ?? b.title)}>
              //         {b.shopify_brand.shopify_collection.store.imageUrl && (
              //           <Image
              //             src={
              //               b.shopify_brand.shopify_collection.store.imageUrl
              //             }
              //             width={400}
              //             height={160}
              //             alt={b.title}
              //           />
              //         )}
              //       </Link>
              //     </div>
              //   );
              // }
              return null;
            })}
        </div>
      </Container>
    </div>
  );
}

const LinkGroup: React.FC<LinkGroupProps> = ({links}) => {
  const indexOfAllGroupsFirstItem =
    links &&
    links.reduce(function (a: any, e: any, i: number) {
      if (e.is_group) a.push(i);
      return a;
    }, []);

  function groupItems(arr: any) {
    const result = arr.reduce(
      (acc: any, item: any) => {
        if (item.is_group) {
          if (acc.currentGroup.length > 0) {
            acc.grouped.push(acc.currentGroup);
            acc.currentGroup = [];
          }
        }
        acc.currentGroup.push(item);
        return acc;
      },
      {grouped: [], currentGroup: []},
    );

    if (result.currentGroup.length > 0) {
      result.grouped.push(result.currentGroup);
    }

    return result.grouped;
  }

  const groupedNavItems = groupItems(links);

  return (
    <div className="flex flex-col justify-between space-y-[60px]">
      {groupedNavItems.map((groupedLinks: any) => {
        return (
          <div
            className="flex flex-col space-y-5 w-full flex-1"
            key={`${groupedLinks[0]._key}-group`}
          >
            {groupedLinks.map((l: any, index: number) => {
              if (l._type === 'linkExternal') {
                return (
                  <div
                    key={l._key}
                    className={clsx([
                      `${
                        l?.is_group ? 'font-semibold uppercase' : 'font-light'
                      }`,
                      // Reserve two text lines for every header so 1- and
                      // 2-line titles end at the same height — keeping each
                      // column's first sub-item on one horizontal row.
                      l?.is_group && 'min-h-[3em] leading-[1.5]',
                      // Indent sub-items so they line up with the group
                      // header's TEXT (which the icon pushes right by
                      // w-5 20px + gap-2 8px = 28px = pl-7), not its icon.
                      !l?.is_group && 'pl-7',
                      'tracking-wide hover:underline',
                    ])}
                  >
                    <a
                      href={l.url}
                      target="_blank"
                      rel={'noopener noreferrer'}
                      className=""
                    >
                      {l.title}
                    </a>
                  </div>
                );
              }
              return (
                <div
                  key={l._key}
                  className={clsx([
                    `${l?.is_group ? 'font-semibold uppercase' : 'font-light'}`,
                    // Reserve two text lines for every header so 1- and
                    // 2-line titles end at the same height — keeping each
                    // column's first sub-item on one horizontal row.
                    l?.is_group && 'min-h-[3em] leading-[1.5]',
                    // Indent sub-items so they line up with the group
                    // header's TEXT (which the icon pushes right by
                    // w-5 20px + gap-2 8px = 28px = pl-7), not its icon.
                    !l?.is_group && 'pl-7',
                    'tracking-wide hover:underline',
                  ])}
                >
                  <Link
                    to={l.slug ? l.slug : ''}
                    className={l?.is_group ? 'flex items-center gap-2' : ''}
                  >
                    {l?.is_group && (
                      <CategoryIcon
                        title={l.title}
                        className="w-5 h-5 shrink-0 text-hk-orange"
                      />
                    )}
                    <span>{l.title}</span>
                  </Link>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

function MegaMenu({
  children,
  className,
}: {
  children: (isOpened: boolean) => JSX.Element;
  className: string;
}) {
  let location = useLocation();

  const [megaMenuIsOpened, setMegaMenuIsOpened] = React.useState(false);

  React.useEffect(() => {
    setMegaMenuIsOpened(false);
  }, [location.pathname]);
  return (
    <div
      className={className}
      onMouseEnter={() => setMegaMenuIsOpened(true)}
      onMouseLeave={() => setMegaMenuIsOpened(false)}
    >
      {children(megaMenuIsOpened)}
    </div>
  );
}

export default MenubarDesktop;
